import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, notificationTemplatesApi } from '../../api/notifications';
import { queryKeys } from '../queryClient';
import type { UpsertTemplateInput } from '../../types/domain';

/** Manager+ only — admin template management (order-confirmation/order-shipped/abandoned-cart). */
export function useNotificationTemplates() {
  return useQuery({
    queryKey: queryKeys.notificationTemplates,
    queryFn: () => notificationTemplatesApi.list(),
  });
}

export function useNotificationTemplate(key: string | null) {
  return useQuery({
    queryKey: queryKeys.notificationTemplate(key ?? ''),
    queryFn: () => notificationTemplatesApi.get(key as string),
    enabled: !!key,
  });
}

export function useUpsertNotificationTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }: { key: string; body: UpsertTemplateInput }) => notificationTemplatesApi.upsert(key, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notificationTemplates }),
  });
}

/** Manager+ only — send a real template to an arbitrary inbox, e.g. to preview an edit. */
export function useTestSendNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { templateKey: string; toEmail: string; sampleData?: Record<string, string | null> }) =>
      notificationsApi.testSend(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-admin'] }),
  });
}

/** Manager+ only — admin-wide notification send log (every customer, every channel/status). */
export function useNotificationsAdmin(filters: { status?: string; channel?: string; cursor?: string; pageSize?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.notificationsAdmin(filters.status, filters.channel, filters.cursor),
    queryFn: () => notificationsApi.listAll(filters),
  });
}

/** Manager+ only — the store team's in-app notifications (new orders, Stripe outcomes), polled every 15 s. The admin bell
 * merges these into its list and toasts new ones; polling rather than SignalR because the source is the durable
 * notification row, so nothing is lost while a tab is closed or a websocket is down. */
export function useStaffNotifications(enabled = true) {
  return useQuery({
    queryKey: ['notifications', 'staff'],
    queryFn: () => notificationsApi.listStaff(30),
    enabled,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}
